/**
 * AI Use Case Register — PDF export.
 *
 * The layout engine lives in `pdfDoc.ts`: a programmatic (not html2canvas) builder that
 * measures every block before placing it, so headings are never orphaned, table rows never
 * straddle a page boundary, and the result is selectable, searchable text. See that file for
 * the full rationale. This module is only the AI-specific composition on top of it.
 */
import type {
  AiUseCase, AiActClassification, AiType, AiUseCaseStatus, TransferMechanism,
} from '../types/models';
import {
  AI_TYPE_LABELS,
  AI_STATUS_LABELS,
  DATA_CATEGORY_LABELS,
  SENSITIVITY_LABELS,
  LEGAL_BASIS_OPTIONS,
  RACI_ROLES,
  dataFieldRiskCell,
} from '../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATION_LABELS,
  AI_ACT_CLASSIFICATION_TONE,
  AI_ACT_CLASSIFICATION_DESCRIPTIONS,
  ACT_ROLE_LABELS,
  GPAI_TIER_LABELS,
  HUMAN_OVERSIGHT_CONTROLS,
  MONITORING_FIELDS,
  REVIEW_FREQUENCY_LABELS,
  APPROVAL_DECISION_LABELS,
  APPROVAL_DECISION_TONE,
  TRANSFER_MECHANISM_LABELS,
  TRANSFER_MECHANISM_TONE,
  gpaiTier,
  needsAutomatedDecisionDetails,
} from '../constants/aiAct';
import { Doc, T, htmlToParagraphs, stripHtml, identityField, type NameLookup, type PdfBuildResult } from './pdfDoc';

const FOOTER_LABEL = 'Mercell Nimbus — AI Use Case Register';

/**
 * The two maps below are local copies, kept from the original port. The AI Act tone maps
 * imported above are deliberately NOT copied a third time: `TONES` in `pdfDoc.ts` uses the
 * same seven names as the `np-tone-*` classes, so `AI_ACT_CLASSIFICATION_TONE` is already
 * expressed in exactly the vocabulary this file needs, and a hand-typed copy of a five-member
 * map would be a fork waiting to drift — a tier recoloured in `aiAct.ts` would keep its old
 * colour in the PDF with nothing to catch it. The two local maps stay as they are rather than
 * being rewritten on the way past; `rejected` is added because the type demands it.
 */
const STATUS_TONE: Record<AiUseCaseStatus, string> = {
  draft: 'neutral', submitted: 'warning', approved: 'info', rejected: 'danger', active: 'success', retired: 'danger',
};
const SENSITIVITY_TONE: Record<string, string> = {
  public: 'success', internal: 'info', confidential: 'warning', restricted: 'danger',
};

// ─── Section renderers ────────────────────────────────────────────────────────

export interface PdfSections {
  identity: boolean;
  /** AI Act risk tier, GPAI, role, ADM/profiling, oversight, vendors, transfers, monitoring
   *  and the approval decision — the whole AI Act position as one togglable block, because a
   *  reader who wants the tier wants the oversight arrangement that goes with it. */
  classification: boolean;
  businessCase: boolean;
  outcomes: boolean;
  dataPrivacy: boolean;
  governance: boolean;
}

export const ALL_SECTIONS: PdfSections = {
  identity: true, classification: true, businessCase: true, outcomes: true,
  dataPrivacy: true, governance: true,
};

const legalBasisLabel = (value: string): string =>
  LEGAL_BASIS_OPTIONS.find(o => o.value === value)?.label || value || '';


/** The stored tier, coalesced. A record written before the field existed has no value, which
 *  is `''` — "not yet classified", a real member of the union and not a missing one. */
const tierOf = (uc: AiUseCase): AiActClassification =>
  ((uc.aiActClassification || '') as AiActClassification);

/** Id eyebrow, title, then status, tier and type chips. */
function useCaseHeading(doc: Doc, uc: AiUseCase): void {
  const tier = tierOf(uc);
  const chips: Array<{ text: string; tone: string }> = [
    { text: AI_STATUS_LABELS[uc.status] ?? uc.status, tone: STATUS_TONE[uc.status] ?? 'neutral' },
    // Always present, including when unclassified: a printed record with no tier chip reads as
    // a record with no risk, which is the opposite of what an empty classification means.
    { text: AI_ACT_CLASSIFICATION_LABELS[tier], tone: AI_ACT_CLASSIFICATION_TONE[tier] },
  ];
  if (uc.aiType) {
    chips.push({ text: AI_TYPE_LABELS[uc.aiType as AiType] ?? uc.aiType, tone: 'primary' });
  }
  doc.recordHeading(uc.useCaseId, uc.name || uc.useCaseId, chips);
}

/** The AI Act position: tier, GPAI, role, ADM, oversight, vendors, transfers, monitoring and
 *  the decision. One section because they are one argument — this is what makes the record
 *  defensible rather than merely complete. */
function renderClassification(doc: Doc, uc: AiUseCase, lookupName?: NameLookup): void {
  const tier = tierOf(uc);
  const description = AI_ACT_CLASSIFICATION_DESCRIPTIONS[tier];

  doc.sectionHeading('AI Act Classification');
  doc.metaRow('Risk tier', `${AI_ACT_CLASSIFICATION_LABELS[tier]}${description.article === '—' ? '' : ` (${description.article})`}`);
  doc.metaRow('Role in the value chain', ACT_ROLE_LABELS[uc.actRole || '']);
  doc.metaRow('General-purpose AI', GPAI_TIER_LABELS[gpaiTier({ isGpai: !!uc.isGpai, isGpaiSystemic: !!uc.isGpaiSystemic })]);
  doc.metaRow('Automated decisions about people', uc.makesAutomatedDecisions ? 'Yes' : 'No');
  doc.metaRow('Profiling', uc.involvesProfiling ? 'Yes' : 'No');
  doc.space(1.5);

  // The decisions flag only — printing this for a profiling-only record produced a permanently
  // blank field, because the server never stores a value unless that flag is set.
  if (needsAutomatedDecisionDetails({ makesAutomatedDecisions: !!uc.makesAutomatedDecisions })) {
    doc.field('What is decided, about whom', uc.automatedDecisionDetails);
  }

  const rationale = htmlToParagraphs(uc.classificationRationale || '');
  if (rationale.length) doc.fieldParagraphs('Why this classification', rationale);
  else doc.field('Why this classification', '');
  doc.metaRow('AI Risk Assessment(s)', (uc.aiRiskAssessmentIds || []).join(', ') || 'Not yet raised');
  doc.space(1);

  // ── Human oversight ──
  const oversight = uc.humanOversight;
  doc.sectionHeading('Human Oversight');
  doc.table(
    ['Control', 'In place'],
    [148, 30],
    HUMAN_OVERSIGHT_CONTROLS.map(control => [
      control.label,
      oversight?.[control.field]
        ? { text: 'Yes', tone: 'success' }
        : { text: 'No', tone: 'danger' },
    ]),
  );
  identityField(doc, 'Accountable person', oversight?.accountablePersonId || '', lookupName);
  doc.field('Escalation path', oversight?.escalationPath || '');
  doc.space(1);

  // ── Vendors and transfers ──
  const vendors = uc.vendors ?? [];
  const transfers = uc.transfers ?? [];
  if (vendors.length || transfers.length) {
    doc.sectionHeading('Vendors & Transfers');
    if (vendors.length) {
      doc.table(
        ['Vendor', 'Model', 'Hosting', 'Trains on ours', 'DPA ref', 'Expiry'],
        [32, 38, 30, 20, 30, 28],
        vendors.map(v => [
          v.name || '—',
          v.modelName || '—',
          v.hostingRegion || '—',
          v.trainsOnOurData ? { text: 'Yes', tone: 'danger' } : { text: 'No', tone: 'success' },
          v.dpaReference || '—',
          v.contractExpiry || '—',
        ]),
      );
      // Sub-processor lists are prose and would wreck a six-column table, so they follow as
      // named fields rather than a seventh column of wrapped text.
      vendors
        .filter(v => (v.subProcessors || '').trim())
        .forEach(v => doc.field(`Sub-processors — ${v.name || 'vendor'}`, v.subProcessors));
    }
    if (transfers.length) {
      doc.table(
        ['Country', 'Recipient', 'Mechanism', 'Safeguards'],
        [38, 48, 48, 44],
        transfers.map(t => {
          const mechanism = (t.mechanism || 'none') as TransferMechanism;
          return [
            t.country || '—',
            t.recipient || '—',
            { text: TRANSFER_MECHANISM_LABELS[mechanism], tone: TRANSFER_MECHANISM_TONE[mechanism] },
            t.safeguards || '—',
          ];
        }),
      );
    }
  }

  // ── Monitoring and review ──
  const monitoring = uc.monitoring;
  doc.sectionHeading('Monitoring & Review');
  doc.metaRow('Review cadence', uc.reviewFrequency ? REVIEW_FREQUENCY_LABELS[uc.reviewFrequency] : 'Not scheduled');
  doc.metaRow('Next review', uc.nextReviewDate || '—');
  doc.space(1.5);
  MONITORING_FIELDS.forEach(({ field, label }) => doc.field(label, monitoring?.[field] || ''));
  doc.space(1);

  // ── The decision ──
  // Only printed when one exists. An "Approved by: —" row on an unapproved record is exactly
  // the false assurance the decision fields were added to remove.
  if (uc.decision) {
    doc.sectionHeading('Decision');
    // A two-column table rather than metaRows, so the decision keeps its tone — an approval
    // with conditions must not print the same as a clean one.
    doc.table(
      ['Decision', 'Decided at'],
      [90, 88],
      [[
        { text: APPROVAL_DECISION_LABELS[uc.decision], tone: APPROVAL_DECISION_TONE[uc.decision] },
        uc.decidedAt ? new Date(uc.decidedAt).toLocaleString('en-GB') : '—',
      ]],
    );
    identityField(doc, 'Decided by', uc.decidedBy || '', lookupName);
    if (uc.decisionConditions) {
      doc.field(uc.decision === 'rejected' ? 'Reason for rejection' : 'Conditions', uc.decisionConditions);
    }
  }
}

function renderUseCase(doc: Doc, uc: AiUseCase, sections: PdfSections, lookupName?: NameLookup): void {
  useCaseHeading(doc, uc);

  if (uc.summary?.trim()) {
    doc.paragraph(uc.summary, T.bodyStrong, { gap: 3 });
  }

  if (sections.identity) {
    doc.sectionHeading('Ownership & Accountability');
    // departmentId is a real foreign key (Step2Identity.tsx), so it goes through the same
    // resolved-id-or-honest-fallback path as 'Decided by' below rather than metaRow's plain
    // string — the id space is shared with names via opts.names, and department ids won't
    // collide with the person/user ids already living there.
    identityField(doc, 'Department', uc.departmentId, lookupName);
    doc.space(1.5);
    // RACI as a real table rather than five long key/value rows. The keys were
    // "Consulted — Legal / Data Protection" and similar, 46-58mm of text against a
    // 46mm gutter, which is what made the columns collide. As a chip plus a short
    // label it fits comfortably, and table() measures every row anyway.
    doc.table(
      ['Role', 'Responsibility', 'Person'],
      [30, 56, 92],
      RACI_ROLES.map(role => {
        const value = (uc as unknown as Record<string, string>)[role.field] || '';
        return [
          { text: role.raci, tone: role.tone || 'neutral' },
          role.label,
          value || 'Not assigned',
        ];
      }),
    );
  }

  // Before the business case, matching the detail page: the tier frames how everything after
  // it reads, and it is the only block whose value changes what may be done with the record.
  if (sections.classification) {
    renderClassification(doc, uc, lookupName);
  }

  if (sections.businessCase) {
    const blocks: Array<[string, string]> = [
      ['Business problem', uc.businessProblem],
      ['Business value', uc.businessValue],
      ['Alternatives considered', uc.alternatives],
    ];
    doc.sectionHeading('Business Case');
    blocks.forEach(([label, html]) => {
      const paras = htmlToParagraphs(html);
      if (paras.length) doc.fieldParagraphs(label, paras);
      else doc.field(label, '');
    });
    doc.space(1);
  }

  if (sections.outcomes && uc.outcomes?.length) {
    doc.sectionHeading('Expected Outcomes');
    doc.table(
      ['Outcome', 'How it is measured', 'Target'],
      [78, 70, 30],
      uc.outcomes.map(o => [o.outcome || '—', o.metric || '—', o.targetDate || '—']),
    );
  }

  if (sections.dataPrivacy) {
    const fields = uc.dataFields ?? [];
    // 'customer_controlled' counts — a non-empty string is truthy regardless of value.
    const hasPersonal = fields.some(f => f.isPersonalData !== 'no');

    doc.sectionHeading('Data & Privacy');
    if (fields.length) {
      doc.table(
        ['Data field', 'Category', 'Personal', 'Sensitivity', 'Purpose', 'Source'],
        // 'PERSONAL' as a header is 14.4mm, which left only 1.6mm against 2mm of cell
        // padding in a 16mm column — the same collision as the RACI keys, just smaller.
        [36, 26, 18, 22, 42, 34],
        fields.map(f => [
          f.name || '—',
          DATA_CATEGORY_LABELS[f.category] ?? f.category,
          dataFieldRiskCell(f),
          { text: SENSITIVITY_LABELS[f.sensitivity] ?? f.sensitivity, tone: SENSITIVITY_TONE[f.sensitivity] ?? 'neutral' },
          f.processingPurpose || '—',
          f.dataSource || '—',
        ]),
      );
    } else {
      doc.paragraph('No data fields recorded.', T.small, { gap: 2 });
    }

    if (hasPersonal) {
      doc.sectionHeading('GDPR Position');
      doc.field('Legal basis (Article 6)', legalBasisLabel(uc.legalBasis));
      doc.field('Why processing personal data is necessary', uc.processingRationale);
      doc.field('Retention', uc.dataRetention);
    }

    doc.field('Shared with third parties', uc.thirdPartySharing ? 'Yes' : 'No');
    if (uc.thirdPartySharing) {
      doc.field('Third-party detail', uc.thirdPartyDetails);
    }
  }

  if (sections.governance) {
    doc.sectionHeading('Record');
    doc.metaRow('Status', AI_STATUS_LABELS[uc.status] ?? uc.status);
    doc.metaRow('Created', uc.createdAt ? new Date(uc.createdAt).toLocaleString('en-GB') : '—');
    doc.metaRow('Last updated', uc.updatedAt ? new Date(uc.updatedAt).toLocaleString('en-GB') : '—');
  }
}

// ─── Public entry point ───────────────────────────────────────────────────────

export type { PdfBuildResult };

/** Build the document without triggering a download. Exported so the pagination
 *  guarantee can be verified headlessly. */
export function buildAiUseCasesPdf(
  useCases: AiUseCase[],
  sections: PdfSections = ALL_SECTIONS,
  /** `names` maps a stored id to a display name — personIds and userIds together, since the
   *  two id spaces do not collide. Anything unresolved is printed as an id under a label that
   *  says so, never as if it were a name. */
  opts: { onePerPage?: boolean; now?: Date; names?: Record<string, string> } = {},
): PdfBuildResult {
  const lookupName: NameLookup | undefined = opts.names
    ? (id: string) => opts.names?.[id]
    : undefined;
  const generatedAt = (opts.now ?? new Date()).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const doc = new Doc(generatedAt, FOOTER_LABEL);
  const plural = useCases.length === 1 ? 'use case' : 'use cases';
  doc.coverTitle('AI Use Case Register', `${useCases.length} ${plural} · generated ${generatedAt}`);

  useCases.forEach((uc, i) => {
    if (i > 0) {
      if (opts.onePerPage !== false) doc.pageBreak();
      else doc.space(6);
    }
    renderUseCase(doc, uc, sections, lookupName);
  });

  const { pdf, pages, maxYByPage, overflows } = doc.finish();
  const filename = useCases.length === 1
    ? `${useCases[0].useCaseId}-ai-use-case.pdf`
    : `ai-use-case-register-${useCases.length}-records.pdf`;

  return { pages, maxYByPage, overflows, filename, save: () => pdf.save(filename) };
}

/** Build and download. */
export function exportAiUseCasesPdf(
  useCases: AiUseCase[],
  sections: PdfSections = ALL_SECTIONS,
  opts: { onePerPage?: boolean; names?: Record<string, string> } = {},
): void {
  buildAiUseCasesPdf(useCases, sections, opts).save();
}

export { stripHtml };
